import { useState, useEffect, useRef } from "react"
import { Loader2, Users } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useInfiniteCommunity } from "@/hooks/useShare"
import { JustifiedRows } from "@/components/common/JustifiedRows"
import { AiGeneratedNotice } from "@/components/common/AiGeneratedNotice"
import { CommunityPreviewModal } from "./CommunityPreviewModal"
import type { SharedImage } from "@/types/share"

export function CommunityTab() {
  const { t } = useTranslation()
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteCommunity(36)
  const observerTarget = useRef<HTMLDivElement>(null)
  const [selectedImage, setSelectedImage] = useState<SharedImage | null>(null)

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { threshold: 0.1 }
    )

    if (observerTarget.current) {
      observer.observe(observerTarget.current)
    }

    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-500">
        <p>{t('community.loadFailed')}</p>
        <p className="text-sm text-slate-400 mt-1">{t('common.tryAgainLater')}</p>
      </div>
    )
  }

  // Flatten all pages into a single array
  const allItems = data?.pages.flatMap((page) => page.items) || []

  if (!allItems.length) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
          <Users className="w-10 h-10 text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          {t('community.emptyTitle')}
        </h3>
        <p className="text-slate-500 text-sm whitespace-pre-line">
          {t('community.emptyDescription')}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 좌→우 행 맞춤 레이아웃 */}
      <JustifiedRows
        items={allItems}
        getKey={(item) => item.id}
        getAspectRatio={(item) => item.aspectRatio}
        renderItem={(item) => (
          <div
            className="cursor-pointer group"
            onClick={() => setSelectedImage(item)}
          >
            <div
              className="relative bg-slate-100 rounded-lg overflow-hidden"
              style={{ aspectRatio: item.aspectRatio.replace(':', '/') }}
            >
              <img
                src={item.imageUrl}
                alt={item.prompt}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />
              {/* AI 생성 표시 (AI 기본법 제31조) */}
              <AiGeneratedNotice variant="badge" className="absolute left-2 top-2 z-10" />
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-200 flex items-end p-3 opacity-0 group-hover:opacity-100">
                <p className="text-white text-sm line-clamp-2">{item.prompt}</p>
              </div>
            </div>
          </div>
        )}
      />

      {/* Infinite scroll trigger */}
      {hasNextPage && (
        <div
          ref={observerTarget}
          className="flex items-center justify-center py-8"
        >
          {isFetchingNextPage && (
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          )}
        </div>
      )}

      {/* Preview Modal */}
      <CommunityPreviewModal
        image={selectedImage}
        onClose={() => setSelectedImage(null)}
      />
    </div>
  )
}
